// Application Insights telemetry module for MouldFlow Surveys PWA
// Provides client-side telemetry with offline persistence via IndexedDB
//
// The connection string is safe for client-side exposure - it only allows
// telemetry ingestion, not data retrieval.

import { ApplicationInsights, IConfiguration, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { OfflineChannel } from '@microsoft/applicationinsights-offlinechannel-js';
import { getSwErrors, clearSwErrors, type SwErrorRecord } from './db-core';

/// <reference path="version.d.ts" />

declare const __APP_VERSION__: string;

// Storage provider constants (from eStorageProviders const enum)
// Using numeric values since const enums are erased at compile time
const STORAGE_PROVIDERS = {
    LocalStorage: 1,
    SessionStorage: 2,
    IndexedDb: 3
} as const;

let appInsights: ApplicationInsights | null = null;
let isInitialized = false;

/**
 * Initializes Application Insights with the provided connection string.
 * Configures offline persistence via IndexedDB for PWA reliability.
 * Safe to call multiple times - subsequent calls are no-ops.
 * 
 * @param connectionString - Application Insights connection string
 */
export function initializeAppInsights(connectionString: string): void {
    if (isInitialized || !connectionString) {
        if (!connectionString) {
            console.log('[Telemetry] No connection string provided, telemetry disabled');
        }
        return;
    }

    try {
        const offlineChannel = new OfflineChannel();

        appInsights = new ApplicationInsights({
            config: {
                connectionString: connectionString,
                
                // SPA routing - tracks navigation automatically
                enableAutoRouteTracking: true,
                
                // Exception tracking
                disableExceptionTracking: false,
                enableUnhandledPromiseRejectionTracking: true,
                
                // Cross-origin correlation for API calls
                enableCorsCorrelation: true,
                enableRequestHeaderTracking: true,
                enableResponseHeaderTracking: true,
                
                // Page view tracking
                autoTrackPageVisitTime: true,
                
                // Disable session storage buffer - offline channel handles persistence
                enableSessionStorageBuffer: false,
                
                // Retry on network failures
                isRetryDisabled: false,
                
                // Configure offline channel for IndexedDB persistence
                extensionConfig: {
                    [offlineChannel.identifier]: {
                        // Prefer IndexedDB for larger storage capacity in PWAs
                        providers: [STORAGE_PROVIDERS.IndexedDb, STORAGE_PROVIDERS.LocalStorage],
                        
                        // 10MB storage for offline telemetry
                        maxStorageSizeInBytes: 10000000,
                        
                        // 14-day retention for apps that may be offline for extended periods
                        inStorageMaxTime: 1209600000,
                        autoClean: true,
                        
                        // Save all events (Normal and Critical)
                        minPersistenceLevel: 1,
                        
                        // Faster sync when back online
                        maxSentBatchInterval: 10000,
                        maxRetry: 3,
                    }
                }
            } as IConfiguration
        });

        appInsights.loadAppInsights();
        
        // Add offline channel after loadAppInsights()
        appInsights.addPlugin(offlineChannel);

        // Set cloud role for Application Map correlation with backend
        appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
            if (envelope.tags) {
                envelope.tags['ai.cloud.role'] = 'MouldFlow.Surveys.Web';
                envelope.tags['ai.cloud.roleInstance'] = 'browser';
            }
            // Add app version to all telemetry
            if (envelope.data) {
                envelope.data['appVersion'] = __APP_VERSION__;
            }
        });

        // Listen for online/offline events
        const offlineListener = offlineChannel.getOfflineListener();
        window.addEventListener('online', () => offlineListener.setOnlineState(1));
        window.addEventListener('offline', () => offlineListener.setOnlineState(2));

        // Listen for service worker error messages
        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
                if (event.data?.type === 'SW_ERROR') {
                    forwardSwErrorToAppInsights(event.data.error);
                }
            });
        }

        isInitialized = true;
        console.log('[Telemetry] Application Insights initialized');
        
        // Check for persisted service worker errors on startup
        checkForPersistedSwErrors();

    } catch (error) {
        console.error('[Telemetry] Failed to initialize Application Insights:', error);
    }
}

/**
 * Checks for service worker errors persisted in IndexedDB and forwards them to App Insights.
 * Called on app startup to capture errors that occurred when the app was closed.
 */
async function checkForPersistedSwErrors(): Promise<void> {
    try {
        const errors = await getSwErrors();
        if (errors.length === 0) return;

        console.log(`[Telemetry] Found ${errors.length} persisted SW errors`);
        
        for (const error of errors) {
            forwardSwErrorToAppInsights(error);
        }
        
        await clearSwErrors();
        console.log('[Telemetry] Persisted SW errors forwarded and cleared');
    } catch (error) {
        console.error('[Telemetry] Failed to check for persisted SW errors:', error);
    }
}

/**
 * Forwards a service worker error to Application Insights.
 */
function forwardSwErrorToAppInsights(error: SwErrorRecord): void {
    if (!appInsights) return;

    appInsights.trackException({
        exception: new Error(error.message),
        properties: {
            source: 'ServiceWorker',
            errorType: error.type,
            swVersion: error.swVersion,
            originalStack: error.stack || '',
            url: error.url || '',
            timestamp: error.timestamp,
            errorId: error.id
        }
    });
}

/**
 * Tracks a page view event.
 * 
 * @param name - Page name
 * @param uri - Optional page URI
 */
export function trackPageView(name: string, uri?: string): void {
    if (!appInsights) return;
    appInsights.trackPageView({ name, uri });
}

/**
 * Tracks a custom event.
 * 
 * @param name - Event name
 * @param properties - Optional custom properties
 */
export function trackEvent(name: string, properties?: Record<string, string>): void {
    if (!appInsights) return;
    appInsights.trackEvent({ name }, properties);
}

/**
 * Tracks an exception with full detail.
 * 
 * @param error - Error object with message, name, and optional stack
 * @param severityLevel - Optional severity level (0=Verbose, 1=Info, 2=Warning, 3=Error, 4=Critical)
 * @param properties - Optional additional properties
 */
export function trackException(
    error: { message: string; name: string; stack?: string; innerException?: string },
    severityLevel?: number,
    properties?: Record<string, string>
): void {
    if (!appInsights) return;
    
    const exception = new Error(error.message);
    exception.name = error.name;
    if (error.stack) {
        exception.stack = error.stack;
    }
    
    // Include detailed properties for better diagnostics
    const errorProperties: Record<string, string> = {
        errorType: error.name,
        errorMessage: error.message,
        // Store full stack as property since AI truncates exception.stack
        fullStack: error.stack || 'No stack trace available',
        url: window.location.href,
        userAgent: navigator.userAgent,
        appVersion: __APP_VERSION__,
        timestamp: new Date().toISOString(),
        ...properties
    };
    
    if (error.innerException) {
        errorProperties.innerException = error.innerException;
    }
    
    appInsights.trackException({ 
        exception, 
        severityLevel,
        properties: errorProperties
    });
}

/**
 * Tracks a trace message.
 * 
 * @param message - Trace message
 * @param severityLevel - Optional severity level
 */
export function trackTrace(message: string, severityLevel?: number): void {
    if (!appInsights) return;
    appInsights.trackTrace({ message, severityLevel });
}

/**
 * Flushes any pending telemetry.
 * Useful before page unload or when switching contexts.
 */
export function flush(): void {
    if (!appInsights) return;
    appInsights.flush();
}

// Expose telemetry API to window for Blazor interop
declare global {
    interface Window {
        telemetry: {
            initialize: typeof initializeAppInsights;
            trackPageView: typeof trackPageView;
            trackEvent: typeof trackEvent;
            trackException: typeof trackException;
            trackTrace: typeof trackTrace;
            flush: typeof flush;
        };
    }
}

window.telemetry = {
    initialize: initializeAppInsights,
    trackPageView,
    trackEvent,
    trackException,
    trackTrace,
    flush
};

// Enhanced global error handler to capture more detail
window.addEventListener('error', (event: ErrorEvent) => {
    if (!appInsights) return;
    
    // Don't double-track if App Insights already caught it
    if ((event as any)._aiHandled) return;
    
    const properties: Record<string, string> = {
        errorType: 'UncaughtException',
        message: event.message || 'Unknown error',
        filename: event.filename || 'unknown',
        lineno: String(event.lineno || 0),
        colno: String(event.colno || 0),
        url: window.location.href,
        userAgent: navigator.userAgent,
        appVersion: __APP_VERSION__,
    };
    
    if (event.error) {
        properties.errorName = event.error.name || 'Error';
        properties.fullStack = event.error.stack || 'No stack';
    }
    
    appInsights.trackException({
        exception: event.error || new Error(event.message),
        severityLevel: 3,
        properties
    });
});

// Enhanced unhandled rejection handler
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (!appInsights) return;
    
    const reason = event.reason;
    const message = reason?.message || String(reason) || 'Unhandled promise rejection';
    const stack = reason?.stack || new Error().stack || 'No stack';
    
    const properties: Record<string, string> = {
        errorType: 'UnhandledRejection',
        message: message,
        fullStack: stack,
        reasonType: typeof reason,
        url: window.location.href,
        userAgent: navigator.userAgent,
        appVersion: __APP_VERSION__,
    };
    
    if (reason && typeof reason === 'object') {
        properties.errorName = reason.name || reason.constructor?.name || 'Unknown';
        // Capture any additional properties on the rejection reason
        try {
            const reasonJson = JSON.stringify(reason, Object.getOwnPropertyNames(reason));
            if (reasonJson.length < 8000) {
                properties.reasonDetails = reasonJson;
            }
        } catch {
            // Ignore serialization errors
        }
    }
    
    appInsights.trackException({
        exception: reason instanceof Error ? reason : new Error(message),
        severityLevel: 3,
        properties
    });
});
